import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import classnames from 'classnames'
import { setIn, useFormikContext } from 'formik'
import { makeStyles } from '@paintscout/ui'

import { getBlockReviewLevel, getPendingReviews, isBlockReviewPending } from './getPendingReviews'
import type { ReviewLevel } from './getPendingReviews'

import type { PresentationBlock, PresentationOptionV2 } from 'paintscout'

// Design tokens from the AI-review prototype (#24250)
const BLUE = '#1668E3'
const BLUE_DARK = '#0F4FB0'
const BLUE_TINT = '#EEF4FE'
const BLUE_TINT_HOVER = '#E1ECFD'
const BLUE_LINE = '#BCD3F7'
const AI_OUTLINE = '#8DB2F0'
const EDIT_OUTLINE = '#8FA3BF'
const EDIT_LINE = '#AEB9C8'
const EDIT_TINT = '#F4F6F9'
const INK = '#1D2433'
const INK_2 = '#46505F'
const BANNER_BG = '#F4F8FE'
const BANNER_LINE = '#DCE6F7'
const FONT = "'Inter', system-ui, sans-serif"
/**
 * Extra space above and below a marked block, so the pill (top -27, 22 high) fits. The canvas puts
 * it on its own block wrapper, not on `ReviewBlock`, so BlockMenu and the add-block buttons, which
 * position against that wrapper, stay level with the block.
 */
export const REVIEW_BLOCK_GAP = 28

const useStyles = makeStyles(
  () => ({
    banner: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '8px 12px',
      padding: '10px 16px',
      backgroundColor: BANNER_BG,
      borderBottom: `1px solid ${BANNER_LINE}`,
      fontFamily: FONT
    },
    bannerLead: { flex: '1 1 300px', minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: 10 },
    bannerIcon: {
      flex: '0 0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 22,
      height: 22,
      borderRadius: 6,
      backgroundColor: BLUE,
      color: '#fff'
    },
    bannerText: { flex: '1 1 auto', minWidth: 0, fontSize: 12.5, lineHeight: 1.5, color: INK_2 },
    bannerStrong: { fontWeight: 600, color: INK },
    bannerActions: { flex: '0 0 auto', display: 'flex', flexWrap: 'wrap', gap: 8, marginLeft: 'auto' },
    button: {
      flex: '0 0 auto',
      height: 28,
      padding: '0 11px',
      borderRadius: 6,
      cursor: 'pointer',
      font: `500 12px ${FONT}`
    },
    buttonSecondary: {
      border: '1px solid #C6D6EE',
      backgroundColor: '#fff',
      color: BLUE,
      '&:hover': { backgroundColor: BANNER_BG }
    },
    buttonPrimary: {
      border: 'none',
      backgroundColor: BLUE,
      color: '#fff',
      '&:hover': { backgroundColor: BLUE_DARK }
    },
    block: { position: 'relative' },
    // The frame reaches 16px above (room for the pill) and 8px on the other sides. Frame and pill
    // z-indexes stay under the page tabs (4) and the fixed top bar (6), so a block scrolled under
    // them does not draw on top. They clear a focused block's border (2).
    frame: { position: 'absolute', inset: '-16px -8px -8px', borderRadius: 4, pointerEvents: 'none', zIndex: 2 },
    frameAi: { border: `1.5px dashed ${AI_OUTLINE}`, backgroundColor: 'rgba(22,104,227,0.04)' },
    frameEdited: { border: `1.5px dashed ${EDIT_OUTLINE}`, backgroundColor: 'rgba(70,80,95,0.03)' },
    pill: {
      position: 'absolute',
      top: -27,
      right: 4,
      zIndex: 3,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 5,
      height: 22,
      padding: '0 9px',
      borderRadius: 11,
      font: `500 11px ${FONT}`,
      whiteSpace: 'nowrap',
      cursor: 'pointer'
    },
    pillAi: {
      border: `1px solid ${BLUE_LINE}`,
      backgroundColor: BLUE_TINT,
      color: BLUE_DARK,
      '&:hover': { backgroundColor: BLUE_TINT_HOVER }
    },
    pillEdited: {
      border: `1px solid ${EDIT_LINE}`,
      backgroundColor: EDIT_TINT,
      color: INK_2,
      '&:hover': { backgroundColor: '#E9EDF2' }
    },
    pillSep: { color: '#7FA3DC' },
    pillSepEdited: { color: '#B4BCC8' }
  }),
  { name: 'ReviewMarks' }
)

interface ReviewMarksContextValue {
  hidden: boolean
  setHidden: (hidden: boolean) => void
  pendingCount: number
  markReviewed: (reviewedPath: string) => void
  markAll: () => void
}

const ReviewMarksContext = createContext<ReviewMarksContextValue | null>(null)

/**
 * Review state for the editor canvas. What is pending is derived from the document itself
 * (`advanced.provenance` without `advanced.reviewed`), so the mark saves and reloads with the
 * presentation and no second store exists. Editing a block never clears its mark (#24250).
 * Reads the presentation from Formik, whose values are the v2 document the editor is changing.
 */
export function ReviewMarksProvider({ children }: { children: React.ReactNode }) {
  const { values, setFieldValue, setValues, submitForm } = useFormikContext<PresentationOptionV2>()
  const [hidden, setHidden] = useState(false)
  const pending = useMemo(() => getPendingReviews(values), [values])

  // `submitForm()` after each set, like every block edit: the editor recomputes `isDirty` only in
  // its `onSubmit`, so without it Save stays disabled. Await the set first; Formik can otherwise
  // submit the old values (see `useFlushPendingEdits`).
  const markReviewed = useCallback(
    async (reviewedPath: string) => {
      await setFieldValue(reviewedPath, true)
      submitForm()
    },
    [setFieldValue, submitForm]
  )
  // One `setValues` rather than a `setFieldValue` per block: one re-render, not one per block.
  const markAll = useCallback(async () => {
    await setValues(pending.reduce((next, { reviewedPath }) => setIn(next, reviewedPath, true), values))
    submitForm()
  }, [pending, values, setValues, submitForm])

  const value = useMemo(
    () => ({ hidden, setHidden, pendingCount: pending.length, markReviewed, markAll }),
    [hidden, pending.length, markReviewed, markAll]
  )

  return <ReviewMarksContext.Provider value={value}>{children}</ReviewMarksContext.Provider>
}

function useReviewMarks(): ReviewMarksContextValue | null {
  return useContext(ReviewMarksContext)
}

/**
 * Returns a check for whether `ReviewBlock` shows a frame and pill on a block. The canvas calls it
 * to add `REVIEW_BLOCK_GAP` to its own block wrapper. False outside a `ReviewMarksProvider`.
 */
export function useIsReviewMarked(): (block: Pick<PresentationBlock, 'advanced'>) => boolean {
  const review = useReviewMarks()
  const hidden = !review || review.hidden
  return useCallback((block) => !hidden && !!getBlockReviewLevel(block) && isBlockReviewPending(block), [hidden])
}

// Three stars: one large outlined, two small filled
const SparkleIcon = ({ size = 10 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path
      d="M9 5.5Q10.1 11.4 15 13Q10.1 14.6 9 20.5Q7.9 14.6 3 13Q7.9 11.4 9 5.5Z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinejoin="round"
    />
    <path d="M18.5 2.5Q19 5.5 21.5 6Q19 6.5 18.5 9.5Q18 6.5 15.5 6Q18 5.5 18.5 2.5ZM18 14.5Q18.5 17 21 17.5Q18.5 18 18 20.5Q17.5 18 15 17.5Q17.5 17 18 14.5Z" />
  </svg>
)

const PenIcon = ({ size = 10 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
)

/** "N parts to review" bar at the top of the editor canvas. Renders nothing when nothing is pending. Mount it only in the editor. */
export function ReviewBanner() {
  const classes = useStyles()
  const review = useReviewMarks()
  if (!review || !review.pendingCount) return null

  const { pendingCount, hidden, setHidden, markAll } = review

  return (
    <div className={classes.banner} role="status" data-testid="review-banner">
      <span className={classes.bannerLead}>
        <span className={classes.bannerIcon}>
          <SparkleIcon size={12} />
        </span>
        <span className={classes.bannerText}>
          <strong className={classes.bannerStrong}>
            {pendingCount} {pendingCount === 1 ? 'part' : 'parts'} to review.
          </strong>{' '}
          Check the highlighted text before sending.
        </span>
      </span>
      <span className={classes.bannerActions}>
        <button
          type="button"
          className={classnames(classes.button, classes.buttonSecondary)}
          onClick={() => setHidden(!hidden)}
        >
          {hidden ? 'Show highlights' : 'Hide highlights'}
        </button>
        <button type="button" className={classnames(classes.button, classes.buttonPrimary)} onClick={markAll}>
          Mark all reviewed
        </button>
      </span>
    </div>
  )
}

function ReviewPill({ level, onClick }: { level: ReviewLevel; onClick: () => void }) {
  const classes = useStyles()
  const isAi = level === 'ai'

  return (
    <button
      type="button"
      data-testid="review-pill"
      className={classnames(classes.pill, isAi ? classes.pillAi : classes.pillEdited)}
      title={
        isAi
          ? "AI wrote this. It isn't on your site, so check it's true for your business."
          : 'AI reworded this from your site. Check the wording still says what you mean.'
      }
      onClick={(ev) => {
        // the block wrapper selects the block on click; the pill only reviews it
        ev.stopPropagation()
        onClick()
      }}
    >
      {isAi ? <SparkleIcon /> : <PenIcon />}
      {isAi ? 'AI written' : 'Reworded by AI'}
      <span className={isAi ? classes.pillSep : classes.pillSepEdited}>·</span> Mark reviewed
    </button>
  )
}

interface ReviewBlockProps {
  /** False outside the editor canvas (preview, customer view): no marks, no wrapper. */
  enabled: boolean
  block: Pick<PresentationBlock, 'advanced'>
  /** Formik path of the block, e.g. `pages[0].sections[1].blocks[2]`. */
  blockPrefix: string
  children: React.ReactNode
}

/**
 * Wraps one canvas block. A block with provenance always gets the wrapper, reviewed or not, so
 * marking it reviewed changes classes only and never remounts the block (which would drop the
 * user's inline-editor focus). A block with no provenance, or one rendered with `enabled` false or
 * outside a `ReviewMarksProvider`, passes through untouched.
 */
export function ReviewBlock({ enabled, block, blockPrefix, children }: ReviewBlockProps) {
  const classes = useStyles()
  const review = useReviewMarks()
  const level = getBlockReviewLevel(block)

  if (!enabled || !review || !level) return <>{children}</>

  const marked = !review.hidden && isBlockReviewPending(block)

  return (
    // `data-review-block` lets the canvas hover rule reach the BlockWrapper inside this div
    <div className={classes.block} data-review-block="">
      {children}
      {marked && (
        <>
          <div className={classnames(classes.frame, level === 'ai' ? classes.frameAi : classes.frameEdited)} />
          <ReviewPill level={level} onClick={() => review.markReviewed(`${blockPrefix}.advanced.reviewed`)} />
        </>
      )}
    </div>
  )
}
